import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { createSession } from "@/lib/api";
import { setHost, setGuest } from "@/lib/store";
import { CURRENCIES } from "@/lib/calc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function CreateSession() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("RON");
  const [revolut, setRevolut] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("Scrie-ți numele");
    setLoading(true);
    try {
      const data = await createSession(name.trim(), currency, revolut.trim());
      const code = data.session.code;
      setHost(code, data.host_id, name.trim());
      setGuest(code, data.host_id, name.trim());
      toast.success("Sesiune creată");
      navigate(`/session/${code}/upload`);
    } catch (e) {
      toast.error("Nu am putut crea sesiunea");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header subtitle="Sesiune nouă" />
      <main className="max-w-md mx-auto px-4 sm:px-6 pt-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Pasul 1</span>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">
            Tu ești cel care a plătit nota
          </h1>
          <p className="text-muted-foreground mt-2">Creează o sesiune și invită-ți prietenii să deconteze.</p>

          <div className="mt-8 space-y-5 bg-card border border-border rounded-[24px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Numele tău</label>
              <input
                data-testid="host-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ex: Andrei"
                className="mt-2 w-full bg-secondary rounded-2xl px-4 py-4 text-lg outline-none focus:ring-2 ring-ring transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Monedă</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="currency-select" className="mt-2 w-full rounded-2xl py-6 text-lg bg-secondary border-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Link Revolut (opțional)</label>
              <input
                data-testid="revolut-link-input"
                value={revolut}
                onChange={(e) => setRevolut(e.target.value)}
                placeholder="ex: revolut.me/andrei"
                className="mt-2 w-full bg-secondary rounded-2xl px-4 py-4 outline-none focus:ring-2 ring-ring transition"
              />
              <p className="text-xs text-muted-foreground mt-2">Prietenii vor putea plăti direct, cu suma completată automat.</p>
            </div>
          </div>

          <button
            data-testid="confirm-create-session-button"
            onClick={handleCreate}
            disabled={loading}
            className="mt-6 w-full bg-primary text-primary-foreground rounded-full py-4 px-6 text-lg font-bold active:scale-[0.98] transition-transform flex justify-center items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <>Continuă <ArrowRight size={20} /></>}
          </button>
        </motion.div>
      </main>
    </div>
  );
}
